import { useState, useEffect, useRef } from 'react';
import { useI18n } from '../context/I18nContext';
import { useApp } from '../context/AppContext';
import { useVoiceCommand } from '../context/VoiceCommandContext';
import { API_BASE } from '../config';
import { detectCountry, getCountryData } from '../utils/geoData';

export default function RegisterScreen({ onBackToLogin }) {
  const { t, lang, switchLang } = useI18n();
  const { setShowHelp, setShowCharter } = useApp();
  const { registerField, unregisterField } = useVoiceCommand();
  
  // Form fields
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [profession, setProfession] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Charter checkboxes
  const [readCharter, setReadCharter] = useState(false);
  const [acceptCharter, setAcceptCharter] = useState(false);
  const [nobleUse, setNobleUse] = useState(false);
  
  // Privacy splash state
  const [countryCode, setCountryCode] = useState(null);
  const countryData = getCountryData(countryCode);
  const [showPrivacySplash, setShowPrivacySplash] = useState(true);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const headingRef = useRef(null);

  useEffect(() => {
    headingRef.current?.focus();
    const detected = detectCountry();
    setCountryCode(detected);
  }, []);

  useEffect(() => {
    registerField('fullName', (val) => setFullName(val));
    registerField('username', (val) => setUsername(val));
    registerField('email', (val) => setEmail(val.replace(/\s+/g, '').replace(/\bat\b/gi, '@').replace(/\bdot\b/gi, '.')));
    registerField('phone', (val) => setPhone(val));
    registerField('profession', (val) => setProfession(val));
    registerField('password', (val) => setPassword(val));
    return () => {
      unregisterField('fullName');
      unregisterField('username');
      unregisterField('email');
      unregisterField('phone');
      unregisterField('profession');
      unregisterField('password');
    };
  }, [registerField, unregisterField]);

  const validateForm = () => {
    if (!fullName.trim()) return t('fullNameRequired');
    if (!username.trim()) return t('usernameRequired');
    if (username.trim().length < 3) return t('usernameTooShort');
    if (!email.trim() || !email.includes('@')) return t('validEmailRequired');
    if (!phone.trim()) return t('phoneRequired');
    if (!gender) return t('genderRequired');
    if (!age || age < 13 || age > 120) return t('validAgeRequired');
    if (!profession.trim()) return t('professionRequired');
    if (!password.trim() || password.length < 6) return t('passwordMin6Chars');
    if (password !== confirmPassword) return t('passwordsDoNotMatch');
    if (!readCharter) return t('mustReadCharter');
    if (!acceptCharter) return t('mustAcceptCharter');
    if (!nobleUse) return t('mustCommitNobleUse');
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: fullName.trim(),
          username: username.trim(),
          email: email.trim(),
          phone: phone.trim(),
          gender,
          age: parseInt(age),
          profession: profession.trim(),
          password: password.trim(),
          lang,
          readCharter: true,
          acceptCharter: true,
          nobleUse: true
        })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || t('registrationFailed'));
      }
    } catch (err) {
      setError(t('networkErrorPleaseTryAgain'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-screen auth-screen" role="main" aria-label={t('register')}>
      <div className="setup-card auth-card" style={{ maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="login-top-bar">
          <img src="/logo-cogency.png" alt="Cogency" className="login-top-logo" />
          <div className="lang-switcher" role="group" aria-label={t('language')}>
            <button className={lang === 'en' ? 'active' : ''} onClick={() => switchLang('en')} aria-pressed={lang === 'en'}>EN</button>
            <button className={lang === 'fr' ? 'active' : ''} onClick={() => switchLang('fr')} aria-pressed={lang === 'fr'}>FR</button>
            <button className={lang === 'ar' ? 'active' : ''} onClick={() => switchLang('ar')} aria-pressed={lang === 'ar'}>عربي</button>
            <button className={lang === 'es' ? 'active' : ''} onClick={() => switchLang('es')} aria-pressed={lang === 'es'}>ES</button>
            <button className={lang === 'de' ? 'active' : ''} onClick={() => switchLang('de')} aria-pressed={lang === 'de'}>DE</button>
            <button className={lang === 'zh' ? 'active' : ''} onClick={() => switchLang('zh')} aria-pressed={lang === 'zh'}>中文</button>
          </div>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>{t('registrationSuccessful')}</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
              {t('pleaseWaitForAdminApproval')}
            </p>
            <button className="btn-primary btn-large" onClick={onBackToLogin}>
              {t('login')}
            </button>
          </div>
        ) : showPrivacySplash ? (
          <div style={{ textAlign: 'center', padding: '20px 16px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: '#f8fafc' }}>
              {t('privacyTitle') || 'Your Privacy Matters'}
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: 1.6 }}>
              {t('privacyText') || 'We respect your privacy. We are committed to not sharing your data with third parties. Your information is used solely to improve your user experience.'}
            </p>
            <div style={{ background: 'rgba(79, 70, 229, 0.08)', borderRadius: '12px', padding: '16px', marginBottom: '20px', textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px' }}>
                <span style={{ color: '#10b981' }}>✓</span>
                <span>{t('privacyNoShare') || 'No data sharing with third parties'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px' }}>
                <span style={{ color: '#10b981' }}>✓</span>
                <span>{t('privacyExperience') || 'Data used only to improve user experience'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                <span style={{ color: '#10b981' }}>✓</span>
                <span>{t('privacySecure') || 'Secure and encrypted storage'}</span>
              </div>
            </div>
            <button
              className="btn-primary btn-large"
              onClick={() => setShowPrivacySplash(false)}
              style={{ width: '100%' }}
            >
              {t('understoodContinue') || 'Understood, Continue'}
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              {/* Full Name */}
              <div className="field-group">
                <label htmlFor="reg-fullname">{t('fullName')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-fullname"
                  type="text"
                  placeholder={countryData?.fullName || t('fullNamePlaceholder')}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                  autoFocus
                  aria-required="true"
                />
              </div>

              {/* Username */}
              <div className="field-group">
                <label htmlFor="reg-username">{t('username')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-username"
                  type="text"
                  placeholder={countryData?.username || t('usernamePlaceholder')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/\s+/g, '').toLowerCase())}
                  disabled={loading}
                  aria-required="true"
                />
              </div>

              {/* Email */}
              <div className="field-group">
                <label htmlFor="reg-email">{t('email')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  aria-required="true"
                />
              </div>

              {/* Phone */}
              <div className="field-group">
                <label htmlFor="reg-phone">{t('phone')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-phone"
                  type="tel"
                  placeholder={countryData?.phone || t('phonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9+\-\s]/g, ''))}
                  disabled={loading}
                  aria-required="true"
                />
              </div>

              {/* Gender */}
              <div className="field-group">
                <label>{t('gender')} <span style={{ color: '#ef4444' }}>*</span></label>
                <div className="gender-options" style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                  <label className="gender-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', border: `1px solid ${gender === 'female' ? '#818cf8' : 'rgba(255,255,255,0.1)'}`, background: gender === 'female' ? 'rgba(129,140,248,0.1)' : 'transparent' }}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={gender === 'female'}
                      onChange={(e) => setGender(e.target.value)}
                      disabled={loading}
                      style={{ accentColor: '#818cf8' }}
                    />
                    <span>{t('female')}</span>
                  </label>
                  <label className="gender-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', border: `1px solid ${gender === 'male' ? '#818cf8' : 'rgba(255,255,255,0.1)'}`, background: gender === 'male' ? 'rgba(129,140,248,0.1)' : 'transparent' }}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={gender === 'male'}
                      onChange={(e) => setGender(e.target.value)}
                      disabled={loading}
                      style={{ accentColor: '#818cf8' }}
                    />
                    <span>{t('male')}</span>
                  </label>
                </div>
              </div>

              {/* Age */}
              <div className="field-group">
                <label htmlFor="reg-age">{t('age')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-age"
                  type="number"
                  min="13"
                  max="120"
                  placeholder={t('agePlaceholder')}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  disabled={loading}
                  aria-required="true"
                />
              </div>

              {/* Profession */}
              <div className="field-group">
                <label htmlFor="reg-profession">{t('profession')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-profession"
                  type="text"
                  placeholder={countryData?.professions || t('professionPlaceholder')}
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  disabled={loading}
                  aria-required="true"
                />
              </div>

              {/* Password */}
              <div className="field-group">
                <label htmlFor="reg-password">{t('password')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-password"
                  type="password"
                  placeholder={t('passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  aria-required="true"
                />
                <small style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{t('passwordMinChars')}</small>
              </div>

              {/* Confirm Password */}
              <div className="field-group">
                <label htmlFor="reg-confirm-password">{t('confirmPassword')} <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  id="reg-confirm-password"
                  type="password"
                  placeholder={t('confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  aria-required="true"
                />
              </div>

              {/* Charter Section */}
              <div className="charter-section" style={{ marginTop: '20px', padding: '16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#f8fafc' }}>{t('charterAgreement')}</h4>
                
                {/* Read Charter */}
                <div className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', fontSize: '13px' }}>
                  <input
                    id="reg-read-charter"
                    type="checkbox"
                    checked={readCharter}
                    onChange={(e) => setReadCharter(e.target.checked)}
                    disabled={loading}
                    style={{ marginTop: '2px', accentColor: '#818cf8', minWidth: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="reg-read-charter" style={{ cursor: 'pointer', lineHeight: 1.4 }}>
                    {t('iHaveReadCharter')}{' '}
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={(e) => { e.preventDefault(); setShowCharter(true); }}
                      style={{ fontSize: '12px', padding: '2px 6px', display: 'inline', color: '#818cf8' }}
                    >
                      {t('readCharter')}
                    </button>
                  </label>
                </div>

                {/* Accept Charter */}
                <div className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', fontSize: '13px' }}>
                  <input
                    id="reg-accept-charter"
                    type="checkbox"
                    checked={acceptCharter}
                    onChange={(e) => setAcceptCharter(e.target.checked)}
                    disabled={loading}
                    style={{ marginTop: '2px', accentColor: '#818cf8', minWidth: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="reg-accept-charter" style={{ cursor: 'pointer', lineHeight: 1.4 }}>
                    {t('iAcceptCharter')}
                  </label>
                </div>

                {/* Noble Use */}
                <div className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px' }}>
                  <input
                    id="reg-noble-use"
                    type="checkbox"
                    checked={nobleUse}
                    onChange={(e) => setNobleUse(e.target.checked)}
                    disabled={loading}
                    style={{ marginTop: '2px', accentColor: '#818cf8', minWidth: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="reg-noble-use" style={{ cursor: 'pointer', lineHeight: 1.4 }}>
                    {t('iCommitNobleUse')}
                  </label>
                </div>
              </div>

              {error && (
                <div id="reg-error" className="setup-error" role="alert" aria-live="assertive" style={{ marginTop: '12px' }}>
                  <p>{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="btn-primary btn-large"
                disabled={loading || !fullName.trim() || !username.trim() || !email.trim() || !phone.trim() || !gender || !age || !profession.trim() || !password.trim() || !confirmPassword.trim() || !readCharter || !acceptCharter || !nobleUse}
                aria-busy={loading}
                style={{ marginTop: '16px' }}
              >
                {loading ? t('registering') : t('register')}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '16px' }}>
              <button className="btn-ghost" onClick={onBackToLogin} style={{ fontSize: '13px' }}>
                ← {t('login')}
              </button>
            </p>

            <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '12px' }}>
              <button className="btn-ghost" onClick={() => setShowHelp(true)} style={{ fontSize: '13px' }}>
                ❓ {t('howItWorks')}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
